import type { Ticker } from 'pixi.js';
import { Container, Text } from 'pixi.js';
import { getLocalizedValue } from '@softgames/localization';
import { Easing } from '../TweenManager/easing';
import type { EasingFunction, EasingMap } from '../TweenManager/easing';
import { TweenManager } from '../TweenManager/TweenManager';
import type { Tween } from '../TweenManager/Tween';

export default function initTweenMixin(ticker: Ticker): void {
  const tweenManager = new TweenManager();
  ticker.add(() => {
    tweenManager.update(ticker.elapsedMS);
  });

  Container.mixin({
    // tweens: tweenManager,
    ownTweenManager: null,

    get tweens(): TweenManager {
      const self = this as Container;
      if (self.ownTweens) {
        return self.ownTweens;
      }
      return self.parent ? self.parent.tweens : tweenManager;
    },

    tweenCreate(target: object): Tween {
      const tween = (this as Container).tweens.create(target);
      (this as Container).once('destroyed', () => {
        tween.stop();
      });
      return tween;
    },

    tweenTo(
      props: object,
      duration = 1000,
      ease: EasingFunction | keyof typeof EasingMap | null = Easing.Default,
      autoStart = false,
      delay = 0,
      repeat = 0,
      yoyo = false,
    ) {
      return (this as Container).tweens.create(this).to(props, duration, ease, autoStart, delay, repeat, yoyo);
    },
    tweenFrom(
      props: object,
      duration = 1000,
      ease: EasingFunction | keyof typeof EasingMap | null = Easing.Default,
      autoStart = false,
      delay = 0,
      repeat = 0,
      yoyo = false,
    ) {
      return (this as Container).tweens.create(this).from(props, duration, ease, autoStart, delay, repeat, yoyo);
    },
    removeTweens(recursive = false) {
      (this as Container).tweens.removeTweensOf(this, recursive);
    },
    pauseTweens(recursive = false) {
      (this as Container).tweens.pauseTweensOf(this as Container, recursive);
    },
    resumeTweens(recursive = false) {
      (this as Container).tweens.resumeTweensOf(this as Container, recursive);
    },
  });

  Text.mixin({
    tweenNumericTextValue(
      settings: {
        from: number;
        to: number;
        template?: { text: string; marker: string };
        roundMethod?: (value: number) => number;
      },
      duration = 1000,
      ease: EasingFunction | keyof typeof EasingMap | null = Easing.Default,
      autoStart = false,
      delay = 0,
    ) {
      const obj = { textValue: settings.from };
      const tween = (this as Text).tweens
        .create(obj)
        .to({ textValue: settings.to }, duration, ease, autoStart, delay, 0, false);

      const text: Text = this as Text;
      const { template } = settings;

      const updateTextValue = (): void => {
        const textValue = settings.roundMethod?.(obj.textValue) ?? obj.textValue;
        const formattedValue = getLocalizedValue(textValue);

        if (template) text.text = template.text.replaceAll(template.marker, formattedValue);
        else text.text = formattedValue;
      };
      tween.onUpdate.add(updateTextValue);
      tween.onComplete.addOnce(updateTextValue);
      return tween;
    },
  });
}
